// HomePage.js (MUI + sx only)
import React, {useEffect, useState} from "react";
import {
	Box,
	Container,
	Typography,
	Button,
	Grid,
	Link,
	Avatar,
	CircularProgress,
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {keyframes} from "@mui/system";
import logo from "../../Logo_No_Bg.png";
import { config } from "../../config";
import {theme} from "../../theme";
import {ThemeProvider} from "@mui/material/styles";
import PlaceIcon from "@mui/icons-material/Place";
import LoginIcon from '@mui/icons-material/Login';

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// Public stores API base URL
const PUBLIC_STORES_API = `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores/publicStores`;

const HomePage = () => {
	const navigate = useNavigate();

	const [isMobile, setIsMobile] = useState(false);

	const [location, setLocation] = useState({
		city: "",
		postcode: "",
		state: "",
		country: "",
		status: "idle", // 'idle' | 'locating' | 'ready' | 'error'
	});

	const [stores, setStores] = useState([]);
	const [storesStatus, setStoresStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'error'
	const [storesError, setStoresError] = useState("");

	// resize handler for header (from old file)
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 600);
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// geolocation + OSM reverse-geocode
	useEffect(() => {
		if (!navigator.geolocation) {
			setLocation((prev) => ({
				...prev,
				status: "error",
			}));
			return;
		}

		setLocation((prev) => ({...prev, status: "locating"}));

		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const {latitude, longitude} = pos.coords;

				// Reverse geocode using OSM Nominatim
				fetch(
					`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
				)
					.then((res) => res.json())
					.then((data) => {
						const addr = data?.address || {};
						const city =
							addr.city ||
							addr.town ||
							addr.village ||
							addr.suburb ||
							addr.hamlet ||
							"";
						const postcode = addr.postcode || "";
						const state = addr.state || "";
						const country = addr.country || "";

						if (!city && !postcode) {
							// Couldn't get meaningful data, treat as error
							setLocation((prev) => ({...prev, status: "error"}));
							return;
						}

						setLocation({
							city,
							postcode,
							state,
							country,
							status: "ready",
						});
					})
					.catch((err) => {
						console.error("Error calling Nominatim:", err);
						setLocation((prev) => ({...prev, status: "error"}));
					});
			},
			(err) => {
				console.error("Geolocation error:", err);
				setLocation((prev) => ({...prev, status: "error"}));
			},
			{
				enableHighAccuracy: false,
				timeout: 10000,
				maximumAge: 600000,
			}
		);
	}, []);

	const hasLocation =
		location.status === "ready" && (location.city || location.postcode);
	const headingCity = hasLocation ? location.city || "your area" : "your city";

	// Helper to build full address for card
	const getStoreAddress = (store) => {
		const parts = [store.addressLine1, store.addressLine2];
		return parts.filter(Boolean).join(", ");
	};

	// Fetch public stores once we have location (city + postalCode etc.)
	useEffect(() => {
		if (location.status !== "ready") return;
	
		const country = location.country || "India";
		const city = location.city || "";
		const postalCode = location.postcode || "";
	
		// If we have neither city nor postalCode, nothing to query with
		if (!city && !postalCode) return;
	
		const fetchStores = async () => {
			setStoresStatus("loading");
			setStoresError("");
	
			try {
				const fetchPromises = [];
	
				// Call 1: country + postalCode
				if (postalCode) {
					const params1 = new URLSearchParams({
						country,
						postalCode,
					});
					const url1 = `${PUBLIC_STORES_API}?${params1.toString()}`;
					fetchPromises.push(
						fetch(url1)
							.then((res) => {
								if (!res.ok) {
									throw new Error(
										`PostalCode query failed. Status: ${res.status}`
									);
								}
								return res.json();
							})
							.catch((err) => {
								console.error("Error in postalCode query:", err);
								// On failure of one branch, return empty list but don't fail everything
								return [];
							})
					);
				}
	
				// Call 2: country and city
				if (city) {
					const params2 = new URLSearchParams({
						country,
						city,
					});
					const url2 = `${PUBLIC_STORES_API}?${params2.toString()}`;
					fetchPromises.push(
						fetch(url2)
							.then((res) => {
								if (!res.ok) {
									throw new Error(`City query failed. Status: ${res.status}`);
								}
								return res.json();
							})
							.catch((err) => {
								console.error("Error in city query:", err);
								return [];
							})
					);
				}
	
				if (fetchPromises.length === 0) {
					setStores([]);
					setStoresStatus("success");
					return;
				}
	
				const results = await Promise.all(fetchPromises);
				const combined = results.flat().filter(Boolean);
	
				// Deduplicate by vendorId
				const seen = new Set();
				const deduped = [];
	
				for (const store of combined) {
					const key = store.vendorId || `${store.businessName}-${store.storefrontWeb}`;
					if (!seen.has(key)) {
						seen.add(key);
						deduped.push(store);
					}
				}
	
				setStores(deduped);
				setStoresStatus("success");
			} catch (err) {
				console.error("Error fetching public stores:", err);
				setStores([]);
				setStoresStatus("error");
				setStoresError(err.message || "Error loading stores");
			}
		};
	
		fetchStores();
	}, [location.status, location.city, location.postcode, location.country]);
	
	return (
		<Box sx={{width: "100%", minHeight: "100vh", userSelect: "none"}}>
			<ThemeProvider theme={theme}>
				{/* HEADER */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "8px 16px",
						backgroundColor: "#fff",
						boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
					}}
				>
					<Link
						sx={{"&:hover": {cursor: "pointer"}}}
						onClick={() => navigate("/")}
						underline="none"
						color="inherit"
					>
						<Box sx={{display: "flex", alignItems: "center", gap: 2}}>
							<img style={{width: "75px", height: "75px"}} src={logo} alt="logo" />
							{!isMobile && (
								<Typography variant="h6" fontWeight="bold">
									MicroStores Merchant Business Center
								</Typography>
							)}
						</Box>
					</Link>
					<Box sx={{display: "flex", alignItems: "center", gap: 2}}>
						<Link
							sx={{fontWeight: 550, fontSize: 18, "&:hover": {cursor: "pointer"}, display:'flex', justifyContent:'center', gap:1, alignItems:'center'}}
							onClick={() => navigate("/login")}
							underline="none"
							color="#000"
						>
							Merchant Login <LoginIcon />
						</Link>
					</Box>
				</Box>

				{/* HERO */}
				<Box
					sx={{
						minHeight: {xs: "auto", md: "70vh"},
						py: {xs: 7.5, md: 10},
						px: 2,
						color: "#000",
						background: "rgb(228, 255, 148)"                // "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
					}}
				>
					<Container maxWidth="lg">
						<Typography
							component="h1"
							sx={{
								fontSize: {xs: "2.2rem", sm: "3rem", md: "3.5rem"},
								fontWeight: 800,
								lineHeight: 1.2,
								textAlign: "center",
								mb: 2.5,
								animation: `${fadeInUp} 0.8s ease-out`,
							}}
						>
							Turn your small retail business into a complete online storefront.
						</Typography>

						<Typography
							component="h2"
							sx={{
								fontSize: {xs: "1.25rem", sm: "1.4rem", md: "1.75rem"},
								fontWeight: 400,
								lineHeight: 1.4,
								textAlign: "center",
								opacity: 0.95,
								mb: 2.5,
								animation: `${fadeInUp} 0.8s ease-out 0.2s backwards`,
							}}
						>
							Bring your physical shop or home business online with a full e-commerce storefront.
						</Typography>

						<Typography
							sx={{
								fontSize: {xs: "1.05rem", md: "1.25rem"},
								lineHeight: 1.7,
								maxWidth: 800,
								mx: "auto",
								textAlign: "center",
								opacity: 0.85,
								mb: 4,
								animation: `${fadeInUp} 0.8s ease-out 0.4s backwards`,
							}}
						>
							Set up your product catalogue, manage orders, and get every payment via UPI straight to
							your account — with no fixed monthly fees.
						</Typography>

						<Box
							sx={{
								display: "flex",
								gap: 2,
								justifyContent: "center",
								mb: 7.5,
								animation: `${fadeInUp} 0.8s ease-out 0.6s backwards`,
								flexDirection: {xs: "column", sm: "row"},
							}}
						>
							<Button
								variant="contained"
								color="secondary"
								onClick={() => navigate("/login")}
								sx={{
									px: 6,
									py: 2.2,
									fontSize: "1.1rem",
									fontWeight: 700,
									borderRadius: 2,
									boxShadow: "0 6px 25px rgba(0,0,0,0.25)",
									transform: "translateY(0)",
									transition:
										"transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, color 0.25s ease",
									"&:hover": {
										transform: "translateY(-4px)",
										boxShadow: "0 10px 35px rgba(0,0,0,0.35)",
										backgroundColor: theme.palette.primary.main,
										color: "#000",
									},
								}}
							>
								Get Started
							</Button>
						</Box>

						{/* Feature highlight */}
						<Box
							sx={{
								maxWidth: 900,
								mx: "auto",
								display: "flex",
								alignItems: "center",
								gap: 2.5,
								borderRadius: 3,
								px: {xs: 3, md: 4},
								py: 3,
								border: "1px solid rgba(255,255,255,0.2)",
								background:"rgba(255,255,255,0.30)",
								backdropFilter: "blur(10px)",
								animation: `${fadeInUp} 0.8s ease-out 0.8s backwards`,
								flexDirection: {xs: "column", sm: "row"},
								textAlign: {xs: "center", sm: "left"},
							}}
						>
							<Box
								sx={{
									width: 48,
									height: 48,
									borderRadius: 2,
									background: "rgba(255,255,255,0.2)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexShrink: 0,
								}}
							>
								<PlaceIcon sx={{width: 30, height: 30}} />
							</Box>
							<Typography
								sx={{
									m: 0,
									fontSize: "1.1rem",
									lineHeight: 1.6,
									opacity: 0.95,
								}}
							>
								As your free storefront partner, Microstores helps you offer free delivery within your
								city limits to your customers.
							</Typography>
						</Box>
					</Container>
				</Box>

				{/* STORES SECTION – NEW DESIGN (based on uploaded CSS layout) */}
				<Box sx={{background: "#f7f9fc", py: {xs: 7.5, md: 10}, px: 2}}>
					<Container maxWidth="lg">
						<Box sx={{textAlign: "center", mb: 6}}>
							<Typography
								component="h2"
								sx={{
									fontSize: {xs: "2rem", md: "2.5rem"},
									fontWeight: 700,
									color: "#2d3748",
									mb: 1.5,
								}}
							>
								Active Microstores in {headingCity}
							</Typography>
							<Typography sx={{fontSize: "1.2rem", color: "#333", mb: 0.5}}>
								Browse local stores in your area
							</Typography>

							<Typography sx={{fontSize: ".95rem", color: "#4A5568", mt: 0.5}}>
								{location.status === "locating" && "Detecting your location…"}
								{location.status === "ready" && hasLocation && (
									<>
										Location: <strong>{location.city || "Your area"}</strong>
										{location.postcode && ` • PIN: ${location.postcode}`}
									</>
								)}
								{location.status === "error" &&
									"We couldn't detect your location. Please allow location access to see nearby stores."}
							</Typography>
						</Box>

						{/* Loading / Error states */}
						{storesStatus === "loading" && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: 120,
								}}
							>
								<CircularProgress size={32} />
								<Typography sx={{ml: 2, color: "#4A5568"}}>
									Loading nearby microstores…
								</Typography>
							</Box>
						)}

						{storesStatus === "error" && (
							<Box sx={{textAlign: "center", color: "#E53E3E", mt: 2}}>
								<Typography variant="body1" sx={{mb: 0.5}}>
									We couldn't load nearby microstores.
								</Typography>
								<Typography variant="body2" sx={{opacity: 0.8}}>
									{storesError || "Please try again in a little while."}
								</Typography>
							</Box>
						)}

						{storesStatus === "success" && stores.length === 0 && (
							<Box sx={{textAlign: "center", color: "#4A5568", mt: 2}}>
								<Typography variant="body1">
									There are no active Microstores near you yet.
								</Typography>
								<Typography variant="body2" sx={{opacity: 0.8, mt: 0.5}}>
									Check back soon – new stores are coming online all the time.
								</Typography>
							</Box>
						)}

						{stores.length > 0 && (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: {
										xs: "1fr",
										md: "repeat(3, 1fr)",
									},
									gap: 3,
									maxWidth: 1400,
									mx: "auto",
								}}
							>
								{stores.map((store) => {
									const firstLetter =
										store.businessName?.charAt(0)?.toUpperCase() || "?";
									const hasLink = !!store.storefrontWeb;

									return (
										<Box
											key={store.vendorId}
											component={hasLink ? "a" : "div"}
											href={hasLink ? store.storefrontWeb : undefined}
											target={hasLink ? "_blank" : undefined}
											rel={hasLink ? "noopener noreferrer" : undefined}
											sx={{
												textDecoration: "none",
												display: "block",
											}}
										>
											<Box
												sx={{
													background: "#fff",
													borderRadius: 2,
													p: 3,
													boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
													border: "1px solid #e2e8f0",
													height: "100%",
													display: "grid",
													gridTemplateColumns: "1fr auto",
													gridTemplateRows: "auto 1fr",
													gap: {xs: 1.5, md: 2},
													transition: "all 0.3s ease",
													cursor: hasLink ? "pointer" : "default",
													"&:hover": hasLink
														? {
																transform: "translateY(-4px)",
																boxShadow:
																	"0 8px 20px rgba(102,126,234,0.15)",
																borderColor: "#667eea",
														  }
														: undefined,
												}}
											>
												{/* Quadrant 1: Name */}
												<Box sx={{gridColumn: "1", gridRow: "1"}}>
													<Typography
														sx={{
															fontSize: "1.25rem",
															fontWeight: 600,
															color: "#2d3748",
															m: 0,
														}}
													>
														{store.businessName}
													</Typography>
												</Box>

												{/* Quadrant 2: Avatar (first letter) */}
												<Box
													sx={{
														gridColumn: "2",
														gridRow: "1",
														display: "flex",
														justifyContent: "center",
														alignItems: "flex-start",
													}}
												>
													<Avatar size="small"
														sx={{
															bgcolor: "#bfff00",
															color: "#2d3748",
															fontWeight: 700,
															fontSize: "1.5rem",
														}}
													>
														{firstLetter}
													</Avatar>
												</Box>

												{/* Quadrant 3: Category, Location, Address */}
												<Box
													sx={{
														gridColumn: "1",
														gridRow: "2",
														display: "flex",
														flexDirection: "column",
														gap: 0.75,
													}}
												>
													<Typography
														sx={{
															fontSize: "0.95rem",
															color: "#667eea",
															fontWeight: 500,
															m: 0,
														}}
													>
														{store.businessCategory}
													</Typography>

													<Typography
														sx={{
															fontSize: "0.9rem",
															color: "#718096",
															display: "flex",
															alignItems: "center",
															m: 0,
														}}
													>
														<Box
															component="span"
															sx={{mr: 0.75, fontSize: "1rem"}}
														>
															📍
														</Box>
														{store.city}
														{store.state ? `, ${store.state}` : ""}
													</Typography>

													<Typography
														sx={{
															fontSize: "0.85rem",
															color: "#718096",
															m: 0,
															pl: 3, // align under location
															lineHeight: 1.4,
														}}
													>
														{getStoreAddress(store)}
													</Typography>
												</Box>

												{/* Quadrant 4: View store link */}
												<Box
													sx={{
														gridColumn: "2",
														gridRow: "2",
														display: "flex",
														alignItems: "flex-end",
														justifyContent: "center",
													}}
												>
													<Typography
														component="span"
														sx={{
															display: "inline-block",
															color: "#667eea",
															fontWeight: 600,
															fontSize: "0.95rem",
															textDecoration: "none",
															transition: "all 0.2s ease",
															...(hasLink && {
																"&:hover": {
																	color: "#5568d3",
																	transform: "translateX(4px)",
																},
															}),
															...(!hasLink && {
																opacity: 0.6,
															}),
														}}
													>
														{hasLink ? "View store →" : "Store coming soon"}
													</Typography>
												</Box>
											</Box>
										</Box>
									);
								})}
							</Box>
						)}
					</Container>
				</Box>

				{/* FOOTER */}
				<Box
					sx={{
						padding: 2,
						backgroundColor: "#e0e0e0",
						textAlign: "center",
					}}
				>
					&copy; {new Date().getFullYear()} BerryChild Inc. All rights reserved.
				</Box>
			</ThemeProvider>
		</Box>
	);
};

export default HomePage;